import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings, Save, Percent, DollarSign, Calendar } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`, { withCredentials: true });
      setSettings(res.data);
    } catch (err) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        bronze_rate: settings.tiers.bronze.rate,
        silver_rate: settings.tiers.silver.rate,
        gold_rate: settings.tiers.gold.rate,
        platinum_rate: settings.tiers.platinum.rate,
        bronze_threshold: settings.tiers.bronze.threshold,
        silver_threshold: settings.tiers.silver.threshold,
        gold_threshold: settings.tiers.gold.threshold,
        platinum_threshold: settings.tiers.platinum.threshold,
        min_payout_amount: settings.min_payout_amount,
        payout_day: settings.payout_day
      };
      
      await axios.put(`${API}/admin/settings`, updateData, { withCredentials: true });
      toast.success("Settings saved successfully");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateTier = (tier, field, value) => {
    setSettings(prev => ({
      ...prev,
      tiers: {
        ...prev.tiers,
        [tier]: {
          ...prev.tiers[tier],
          [field]: field === "rate" ? parseFloat(value) || 0 : parseInt(value) || 0
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const tierColors = {
    bronze: "from-amber-600 to-amber-700",
    silver: "from-slate-400 to-slate-500",
    gold: "from-yellow-500 to-yellow-600",
    platinum: "from-purple-500 to-purple-600"
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Settings className="h-7 w-7 text-emerald-500" />
            Program Settings
          </h1>
          <p className="text-slate-400 mt-1">Configure commission tiers and payout settings</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          data-testid="save-settings-btn"
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Commission Tiers */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Percent className="h-5 w-5 text-emerald-500" />
            Commission Tiers
          </CardTitle>
          <CardDescription>Set commission rates and referral thresholds for each tier</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(settings?.tiers || {}).map(([tier, data]) => (
              <div 
                key={tier}
                className={`p-4 rounded-lg bg-gradient-to-br ${tierColors[tier]} bg-opacity-20`}
              >
                <h3 className="text-lg font-semibold text-white capitalize mb-4">{tier}</h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-slate-200 text-sm">Commission Rate (%)</Label>
                    <Input
                      type="number"
                      value={data.rate}
                      onChange={(e) => updateTier(tier, "rate", e.target.value)}
                      className="mt-1 bg-slate-900/50 border-slate-600 text-white"
                      data-testid={`${tier}-rate-input`}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-200 text-sm">Min Referrals Required</Label>
                    <Input
                      type="number"
                      value={data.threshold}
                      onChange={(e) => updateTier(tier, "threshold", e.target.value)}
                      className="mt-1 bg-slate-900/50 border-slate-600 text-white"
                      data-testid={`${tier}-threshold-input`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Payout Settings */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-500" />
            Payout Settings
          </CardTitle>
          <CardDescription>Configure minimum payout amounts and schedule</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-slate-300">Minimum Payout Amount ($)</Label>
              <Input
                type="number"
                value={settings?.min_payout_amount || 50}
                onChange={(e) => setSettings(prev => ({ ...prev, min_payout_amount: parseFloat(e.target.value) || 0 }))}
                className="mt-1 bg-slate-700 border-slate-600 text-white"
                data-testid="min-payout-input"
              />
              <p className="text-xs text-slate-500 mt-1">Partners must earn at least this amount before requesting payout</p>
            </div>
            <div>
              <Label className="text-slate-300">Payout Day of Month</Label>
              <Input
                type="number"
                min="1"
                max="28"
                value={settings?.payout_day || 1}
                onChange={(e) => setSettings(prev => ({ ...prev, payout_day: parseInt(e.target.value) || 1 }))}
                className="mt-1 bg-slate-700 border-slate-600 text-white"
                data-testid="payout-day-input"
              />
              <p className="text-xs text-slate-500 mt-1">Day of the month when payouts are processed (1-28)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Last Updated */}
      {settings?.updated_at && (
        <p className="text-sm text-slate-500 flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Last updated: {new Date(settings.updated_at).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default AdminSettings;
